// The kind of a document a referrer attaches to a referral (PPL-3549).
// Deliberately small: a slug is added only when a consumer branches on it.
export enum ReferralDocumentType {
  // The form the patient signed to consent to BEING REFERRED to Perci — the
  // documentary evidence of what the referrer confirms on the form itself
  // ("I confirm I have consent from the patient to refer them to Perci
  // Health"). A signed document, not to be confused with `consent.patient` on
  // the referral, which is the patient's go-ahead for the referral to PROCEED
  // once we have contacted them (ReferralConsentSchema).
  patientReferralConsent = 'patient-referral-consent',
  // The clinical report the AI care-document generation reads. A referral can
  // collect several of them over time (a case manager sends an updated one),
  // stored in the order they were attached. L&G is the exception: its form
  // offers a single "Latest clinical report" slot, so an L&G referral carries
  // exactly one.
  clinicalReport = 'clinical-report',
  // Everything else the referrer sends ("Supporting Documents" in the Healix
  // spec): referral forms, GP letters, scans. Assumed when no type is sent.
  supporting = 'supporting',
}

// The type a document without an explicit `type` is stored as, so a stored
// document always carries a concrete type.
export const defaultReferralDocumentType = ReferralDocumentType.supporting;

// The types a referral holds at most once — a second document of the same
// type in one payload is rejected rather than silently kept as the only one.
// Only the patient's consent to the referral: one signed form per referral,
// where a second would mean the referrer sent the wrong file.
// Clinical reports and supporting documents accumulate.
export const singletonReferralDocumentTypes: ReferralDocumentType[] = [
  ReferralDocumentType.patientReferralConsent,
];

export function displayReferralDocumentType(value: ReferralDocumentType) {
  switch (value) {
    case ReferralDocumentType.patientReferralConsent:
      return 'Patient consent to referral';
    case ReferralDocumentType.clinicalReport:
      return 'Clinical report';
    case ReferralDocumentType.supporting:
      return 'Supporting document';
    default:
      throw new Error(`Unknown referral document type: ${value}`);
  }
}

export function isReferralDocumentType(
  value: unknown,
): value is ReferralDocumentType {
  return Object.values<unknown>(ReferralDocumentType).includes(value);
}
